import React, { useState, useEffect } from "react";
import { Star, TrendingUp, TrendingDown, Package } from "lucide-react";
import { getMetalRates, formatCurrencyINR } from "../services/metalRates";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../services/firebase";
import Header from "./Header";
import Footer from "./Footer";
import "./Dashboard.css";
import { useNavigate } from "react-router-dom";
import { useSeller } from "../contexts/SellerContext";
import { useOrderOperations } from "../hooks/useOrderOperations";
import CatalogueCard from "./CatalogueCard";

const parseNum = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Get time value in milliseconds with full support for seconds, strings, and timestamps
const getTimeValue = (value) => {
  if (!value) return 0;
  if (typeof value === "number") {
    if (value < 10000000000) return value * 1000;
    return value;
  }
  if (value?.seconds) return value.seconds * 1000;
  if (value?._seconds) return value._seconds * 1000;
  if (value?.toDate && typeof value.toDate === "function")
    return value.toDate().getTime();
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// Extract best timestamp from an order
const getOrderTimestamp = (order) => {
  return (
    getTimeValue(order.createdAt) ||
    getTimeValue(order.orderCreatedAt) ||
    getTimeValue(order.timestamp) ||
    getTimeValue(order.orderDate) ||
    getTimeValue(order.date) ||
    getTimeValue(order.createdDate) ||
    getTimeValue(order.updatedAt) ||
    getTimeValue(order.settlementTime) ||
    0
  );
};

// Calculate fine weight for an order
const getOrderFineWeight = (order) => {
  const directFine = parseNum(
    order.fineWeight ||
      order.totalFine ||
      order.fineWt ||
      order.totalFineWeight,
  );
  if (directFine > 0) return directFine;

  const netWt = parseNum(
    order.netWt || order.displayNetWt || order.totalNetWt,
  );
  const wastage = parseNum(order.wastage);
  const purity = parseNum(order.purity);
  const specificationMC = parseNum(order.specificationMC);

  // If no valid data, return 0
  if (netWt === 0) return 0;

  // Fine weight = net weight * (purity + wastage + specificationMC) / 100
  const addOnFactor = (purity + wastage + specificationMC) / 100;
  return netWt * addOnFactor;
};

// Calculate making charges for an order
const getOrderMc = (order) => {
  // 1. Direct MC field
  const directMc = parseNum(
    order.totalMc ||
      order.totalMC ||
      order.makingCharges ||
      order.makingCharge ||
      order.mc,
  );
  if (directMc > 0) return directMc;

  // 2. Computed from rates
  const netWt = parseNum(
    order.netWt || order.displayNetWt || order.totalNetWt,
  );
  const qty =
    parseNum(
      order.orderPiece ||
        order.quantity ||
        order.orderQuantity ||
        order.totalPieces,
    ) || 1;
  const specWt = parseNum(order.specificationWt || order.specWt) || netWt;
  const setMc = parseNum(order.setMc || order.setMakingCharge);
  const gramMc = parseNum(
    order.netGramMc || order.gramMc || order.makingChargePerGram,
  );
  const specMc = parseNum(
    order.specificationGramRate || order.specMcPerGram || order.specGramRate,
  );

  return setMc * qty + gramMc * netWt + specMc * specWt;
};

const Dashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("Today");
  const [liveRates, setLiveRates] = useState(null);
  const [previousRates, setPreviousRates] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [loadingBusinessData, setLoadingBusinessData] = useState(true);
  const navigate = useNavigate();
  const { seller } = useSeller();
  const { calculateOrderMetrics } = useOrderOperations();

  const currentData = businessData?.[selectedPeriod] || {
    sales: {
      fine: "0.000g",
      mc: "₹0",
      value: "0.000g • ₹0",
      change: 0,
      trend: "flat",
    },
    orders: { value: "0", change: 0, trend: "flat" },
    received: {
      fine: "0.000g",
      mc: "₹0",
      value: "0.000g • ₹0",
      change: 0,
      trend: "flat",
    },
  };

  // Calculate rate change
  const calculateChange = (current, previous) => {
    if (!current || !previous || previous === 0) {
      return { amount: 0, percentage: 0, direction: "flat" };
    }
    const change = current - previous;
    const percentage = ((change / previous) * 100).toFixed(2);
    return {
      amount: change,
      percentage: Math.abs(Number(percentage)).toFixed(2),
      direction: change > 0 ? "up" : change < 0 ? "down" : "flat",
    };
  };

  // Format rupee values
  const formatRupee = (amount) =>
    `₹${parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;

  // Format weight values
  const formatWeight = (val) => `${parseFloat(val || 0).toFixed(3)}g`;

  // Fetch business overview data from orders
  useEffect(() => {
    const fetchBusinessData = async () => {
      console.log("[Dashboard] ========== STARTING DATA FETCH ==========");

      if (!seller?.sellerId) {
        console.log("[Dashboard] ❌ No seller ID, skipping fetch");
        console.log("[Dashboard] Seller object:", seller);
        setLoadingBusinessData(false);
        return;
      }

      try {
        setLoadingBusinessData(true);
        console.log("[Dashboard] ✅ Seller ID:", seller.sellerId);
        console.log(
          "[Dashboard] Seller state:",
          seller?.state,
          "segment:",
          seller?.segment,
        );
        console.log("[Dashboard] Full seller object:", seller);

        // Get current RTGS sale rate for the seller's state
        const rates = await getMetalRates({
          useLiveApi: true,
          state: seller?.state,
        });

        console.log("[Dashboard] Fetched rates:", rates);

        const metalType = seller?.segment?.toLowerCase() || "gold";
        const rtgsSaleRate = rates?.[metalType]?.RTGS_SALE_RATE || 0;

        console.log(
          "[Dashboard] Metal type:",
          metalType,
          "RTGS Sale Rate:",
          rtgsSaleRate,
        );

        if (!rtgsSaleRate) {
          console.warn(
            "[Dashboard] No RTGS sale rate available, using fallback",
          );
        }

        // Fetch ALL orders directly by sellerId (not limited to settlements)
        console.log("[Dashboard] Fetching ALL orders directly by sellerId...");
        console.log(
          "[Dashboard] Query: orderList WHERE sellerId ==",
          seller.sellerId,
        );

        let orders = [];
        try {
          const allOrdersQuery = query(
            collection(db, "orderList"),
            where("sellerId", "==", seller.sellerId),
          );
          console.log("[Dashboard] Executing Firestore query...");
          const allOrdersSnap = await getDocs(allOrdersQuery);
          console.log(
            "[Dashboard] Query completed. Documents found:",
            allOrdersSnap.size,
          );

          allOrdersSnap.forEach((docSnap) => {
            orders.push({ firestoreId: docSnap.id, ...docSnap.data() });
          });
        } catch (queryError) {
          console.error("[Dashboard] ❌ Error fetching orders:", queryError);
          console.error("[Dashboard] Error details:", queryError.message);
          throw queryError;
        }

        console.log("[Dashboard] Fetched ALL orders:", orders.length);
        if (orders.length > 0) {
          console.log("[Dashboard] ===== FIRST 3 ORDERS SAMPLE =====");
          orders.slice(0, 3).forEach((order, idx) => {
            console.log(`[Dashboard] Order ${idx + 1}:`, {
              id: order.firestoreId,
              sellerId: order.sellerId,
              netWt: order.netWt,
              displayNetWt: order.displayNetWt,
              totalNetWt: order.totalNetWt,
              purity: order.purity,
              wastage: order.wastage,
              specificationMC: order.specificationMC,
              orderStatus: order.orderStatus,
              status: order.status,
              createdAt: order.createdAt,
              orderCreatedAt: order.orderCreatedAt,
            });
          });
          console.log("[Dashboard] ======================================");
        } else {
          console.warn(
            "[Dashboard] ❌ NO ORDERS FOUND for seller:",
            seller.sellerId,
          );
          console.log("[Dashboard] Check Firestore console:");
          console.log("[Dashboard] 1. Go to Firebase Console → Firestore");
          console.log("[Dashboard] 2. Open 'orderList' collection");
          console.log(
            "[Dashboard] 3. Check if any documents have sellerId =",
            seller.sellerId,
          );
        }

        const now = Date.now();
        const oneDayAgo = now - 24 * 60 * 60 * 1000;
        const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
        const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

        console.log("[Dashboard] Time ranges:");
        console.log("[Dashboard] Now:", new Date(now).toISOString());
        console.log(
          "[Dashboard] 1 day ago:",
          new Date(oneDayAgo).toISOString(),
        );
        console.log(
          "[Dashboard] 7 days ago:",
          new Date(sevenDaysAgo).toISOString(),
        );
        console.log(
          "[Dashboard] 30 days ago:",
          new Date(thirtyDaysAgo).toISOString(),
        );

        // Calculate metrics for each period with optional endTime window boundary
        const calculateMetrics = (startTime, endTime = null) => {
          const filteredOrders = orders.filter((order) => {
            if (!startTime) return true;
            const orderTime = getOrderTimestamp(order);
            if (orderTime === 0) {
              return !endTime && startTime <= thirtyDaysAgo;
            }
            if (endTime) {
              return orderTime >= startTime && orderTime < endTime;
            }
            return orderTime >= startTime;
          });

          console.log(
            `[Dashboard] Filtered orders for period (startTime: ${new Date(
              startTime,
            ).toISOString()}${endTime ? `, endTime: ${new Date(endTime).toISOString()}` : ""}):`,
            filteredOrders.length,
          );

          let totalOrders = 0;
          let salesFine = 0;
          let salesMc = 0;
          let receivedFine = 0;
          let receivedMc = 0;

          filteredOrders.forEach((order) => {
            const status = (
              order.orderStatus ||
              order.status ||
              ""
            ).toLowerCase();

            // Calculate fine weight and MC for this order using consistent methods
            const fineWt = getOrderFineWeight(order);
            const mc = getOrderMc(order);

            console.log(
              `[Dashboard] Order ${
                order.firestoreId
              }: status="${status}", fine=${fineWt.toFixed(
                3,
              )}g, mc=₹${mc.toFixed(2)}, sellerPaymentVerified=${
                order.sellerPaymentVerified
              }`,
            );

            // 1. ORDERS & SALES GROUP:
            // Any order excluding status "requested" is in sales and orders group
            const isSalesOrOrder = !status.includes("requested");
            if (isSalesOrOrder) {
              totalOrders++;
              salesFine += fineWt;
              salesMc += mc;
            }

            // 2. RECEIVED / IS_SETTLED GROUP:
            // "if then it is isSettled order.sellerPaymentVerified ===true or recived group"
            const isSettled =
              order.sellerPaymentVerified === true ||
              order.settlement?.sellerPaymentVerified === true ||
              order.paymentVerified === true ||
              status === "settled" ||
              status.includes("settled");

            if (isSettled) {
              receivedFine += fineWt;
              receivedMc += mc;
            }
          });

          console.log(
            `[Dashboard] Period metrics: Sales Fine=${salesFine.toFixed(
              3,
            )}g, Sales MC=₹${salesMc.toFixed(
              2,
            )}, Orders=${totalOrders}, Received Fine=${receivedFine.toFixed(
              3,
            )}g, Received MC=₹${receivedMc.toFixed(2)}`,
          );

          return { totalOrders, salesFine, salesMc, receivedFine, receivedMc };
        };

        // Calculate for each period (Current window)
        const todayMetrics = calculateMetrics(oneDayAgo, now);
        const weekMetrics = calculateMetrics(sevenDaysAgo, now);
        const monthMetrics = calculateMetrics(thirtyDaysAgo, now);

        console.log("[Dashboard] Today metrics:", todayMetrics);
        console.log("[Dashboard] Week metrics:", weekMetrics);
        console.log("[Dashboard] Month metrics:", monthMetrics);

        // Calculate previous period metrics for comparison (Previous window)
        const yesterdayMetrics = calculateMetrics(
          oneDayAgo - 24 * 60 * 60 * 1000,
          oneDayAgo,
        );
        const prevWeekMetrics = calculateMetrics(
          sevenDaysAgo - 7 * 24 * 60 * 60 * 1000,
          sevenDaysAgo,
        );
        const prevMonthMetrics = calculateMetrics(
          thirtyDaysAgo - 30 * 24 * 60 * 60 * 1000,
          thirtyDaysAgo,
        );

        // Calculate changes and trends for all periods (1 day, 7 days, 30 days)
        const calculateTrend = (current, previous) => {
          if (!previous || previous === 0) {
            if (current > 0) return { change: 100, trend: "up" };
            return { change: 0, trend: "flat" };
          }
          if (current === previous) return { change: 0, trend: "flat" };
          const changePercent = ((current - previous) / previous) * 100;
          return {
            change: Math.abs(changePercent).toFixed(1),
            trend: changePercent > 0 ? "up" : "down",
          };
        };

        const todaySalesTrend = calculateTrend(
          todayMetrics.salesFine,
          yesterdayMetrics.salesFine,
        );
        const todayOrdersTrend = calculateTrend(
          todayMetrics.totalOrders,
          yesterdayMetrics.totalOrders,
        );
        const todayReceivedTrend = calculateTrend(
          todayMetrics.receivedFine,
          yesterdayMetrics.receivedFine,
        );

        const weekSalesTrend = calculateTrend(
          weekMetrics.salesFine,
          prevWeekMetrics.salesFine,
        );
        const weekOrdersTrend = calculateTrend(
          weekMetrics.totalOrders,
          prevWeekMetrics.totalOrders,
        );
        const weekReceivedTrend = calculateTrend(
          weekMetrics.receivedFine,
          prevWeekMetrics.receivedFine,
        );

        const monthSalesTrend = calculateTrend(
          monthMetrics.salesFine,
          prevMonthMetrics.salesFine,
        );
        const monthOrdersTrend = calculateTrend(
          monthMetrics.totalOrders,
          prevMonthMetrics.totalOrders,
        );
        const monthReceivedTrend = calculateTrend(
          monthMetrics.receivedFine,
          prevMonthMetrics.receivedFine,
        );

        console.log("[Dashboard] ===== FINAL CALCULATION SUMMARY =====");
        console.log("[Dashboard] Total Orders Found:", orders.length);
        console.log(
          "[Dashboard] Today - Sales Fine:",
          formatWeight(todayMetrics.salesFine),
          "MC:",
          formatRupee(todayMetrics.salesMc),
          "Orders:",
          todayMetrics.totalOrders,
          "Received Fine:",
          formatWeight(todayMetrics.receivedFine),
          "MC:",
          formatRupee(todayMetrics.receivedMc),
        );
        console.log(
          "[Dashboard] 7D - Sales Fine:",
          formatWeight(weekMetrics.salesFine),
          "MC:",
          formatRupee(weekMetrics.salesMc),
          "Orders:",
          weekMetrics.totalOrders,
          "Received Fine:",
          formatWeight(weekMetrics.receivedFine),
          "MC:",
          formatRupee(weekMetrics.receivedMc),
        );
        console.log(
          "[Dashboard] 30D - Sales Fine:",
          formatWeight(monthMetrics.salesFine),
          "MC:",
          formatRupee(monthMetrics.salesMc),
          "Orders:",
          monthMetrics.totalOrders,
          "Received Fine:",
          formatWeight(monthMetrics.receivedFine),
          "MC:",
          formatRupee(monthMetrics.receivedMc),
        );
        console.log("[Dashboard] ========================================");

        setBusinessData({
          Today: {
            sales: {
              fine: formatWeight(todayMetrics.salesFine),
              mc: formatRupee(todayMetrics.salesMc),
              value: `${formatWeight(todayMetrics.salesFine)} • ${formatRupee(
                todayMetrics.salesMc,
              )}`,
              change: todaySalesTrend.change,
              trend: todaySalesTrend.trend,
            },
            orders: {
              value: Math.round(todayMetrics.totalOrders).toString(),
              change: todayOrdersTrend.change,
              trend: todayOrdersTrend.trend,
            },
            received: {
              fine: formatWeight(todayMetrics.receivedFine),
              mc: formatRupee(todayMetrics.receivedMc),
              value: `${formatWeight(
                todayMetrics.receivedFine,
              )} • ${formatRupee(todayMetrics.receivedMc)}`,
              change: todayReceivedTrend.change,
              trend: todayReceivedTrend.trend,
            },
          },
          "7D": {
            sales: {
              fine: formatWeight(weekMetrics.salesFine),
              mc: formatRupee(weekMetrics.salesMc),
              value: `${formatWeight(weekMetrics.salesFine)} • ${formatRupee(
                weekMetrics.salesMc,
              )}`,
              change: weekSalesTrend.change,
              trend: weekSalesTrend.trend,
            },
            orders: {
              value: Math.round(weekMetrics.totalOrders).toString(),
              change: weekOrdersTrend.change,
              trend: weekOrdersTrend.trend,
            },
            received: {
              fine: formatWeight(weekMetrics.receivedFine),
              mc: formatRupee(weekMetrics.receivedMc),
              value: `${formatWeight(weekMetrics.receivedFine)} • ${formatRupee(
                weekMetrics.receivedMc,
              )}`,
              change: weekReceivedTrend.change,
              trend: weekReceivedTrend.trend,
            },
          },
          "30D": {
            sales: {
              fine: formatWeight(monthMetrics.salesFine),
              mc: formatRupee(monthMetrics.salesMc),
              value: `${formatWeight(monthMetrics.salesFine)} • ${formatRupee(
                monthMetrics.salesMc,
              )}`,
              change: monthSalesTrend.change,
              trend: monthSalesTrend.trend,
            },
            orders: {
              value: Math.round(monthMetrics.totalOrders).toString(),
              change: monthOrdersTrend.change,
              trend: monthOrdersTrend.trend,
            },
            received: {
              fine: formatWeight(monthMetrics.receivedFine),
              mc: formatRupee(monthMetrics.receivedMc),
              value: `${formatWeight(
                monthMetrics.receivedFine,
              )} • ${formatRupee(monthMetrics.receivedMc)}`,
              change: monthReceivedTrend.change,
              trend: monthReceivedTrend.trend,
            },
          },
        });
      } catch (error) {
        console.error("[Dashboard] Error fetching business data:", error);
        // Set default empty data on error
        setBusinessData({
          Today: {
            sales: {
              fine: "0.000g",
              mc: "₹0",
              value: "0.000g • ₹0",
              change: 0,
              trend: "flat",
            },
            orders: { value: "0", change: 0, trend: "flat" },
            received: {
              fine: "0.000g",
              mc: "₹0",
              value: "0.000g • ₹0",
              change: 0,
              trend: "flat",
            },
          },
          "7D": {
            sales: {
              fine: "0.000g",
              mc: "₹0",
              value: "0.000g • ₹0",
              change: 0,
              trend: "flat",
            },
            orders: { value: "0", change: 0, trend: "flat" },
            received: {
              fine: "0.000g",
              mc: "₹0",
              value: "0.000g • ₹0",
              change: 0,
              trend: "flat",
            },
          },
          "30D": {
            sales: {
              fine: "0.000g",
              mc: "₹0",
              value: "0.000g • ₹0",
              change: 0,
              trend: "flat",
            },
            orders: { value: "0", change: 0, trend: "flat" },
            received: {
              fine: "0.000g",
              mc: "₹0",
              value: "0.000g • ₹0",
              change: 0,
              trend: "flat",
            },
          },
        });
      } finally {
        setLoadingBusinessData(false);
      }
    };

    fetchBusinessData();
  }, [seller?.sellerId, seller?.state, seller?.segment, seller, calculateOrderMetrics]);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        // Get previous rates from localStorage
        const storedRates = localStorage.getItem("sellerDashboardRates");
        if (storedRates) {
          setPreviousRates(JSON.parse(storedRates));
        }

        // Fetch new rates with seller's state
        const rates = await getMetalRates({
          useLiveApi: true,
          state: seller?.state,
        });
        setLiveRates(rates);

        // Store current rates for next comparison
        if (rates) {
          localStorage.setItem(
            "sellerDashboardRates",
            JSON.stringify({
              gold: rates.gold?.RTGS_SALE_RATE,
              silver: rates.silver?.RTGS_SALE_RATE,
              timestamp: Date.now(),
            }),
          );
        }
      } catch (error) {
        console.error("Error fetching live rates:", error);
      }
    };
    fetchRates();

    // Refresh every 5 seconds
    const interval = setInterval(fetchRates, 5000);
    return () => clearInterval(interval);
  }, [seller?.state]);

  return (
    <>
      <Header title="Dashboard" />
      <div className="dashboard-container">
        <main className="dashboard-main">
          <div className="card profile-card">
            <div className="profile-icon">
              {seller?.logourl ? (
                <img
                  src={seller.logourl}
                  alt="Store Logo"
                  className="store-logo"
                />
              ) : (
                <Star size={24} color="white" />
              )}
            </div>
            <div className="profile-info">
              <h2 className="profile-title">
                {seller?.organizationName || "Store"}
              </h2>
              <p className="profile-subtitle">View profile & settings</p>
            </div>
          </div>

          <div className="card">
            <div className="overview-header">
              <h3 className="overview-title">Business Overview</h3>
              <div className="tab-buttons">
                {["Today", "7D", "30D"].map((period) => (
                  <button
                    key={period}
                    className={`tab-button ${
                      selectedPeriod === period ? "tab-active" : "tab-inactive"
                    }`}
                    onClick={() => setSelectedPeriod(period)}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>

            {loadingBusinessData ? (
              <div className="metrics-loading">
                <p>Loading business metrics...</p>
              </div>
            ) : (
              <div className="metrics-row">
                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-left">
                      <div className="metric-icon sales-icon-bg">
                        <span className="metric-icon-text">Rs</span>
                      </div>
                      <div className="metric-info">
                        <div className="metric-dual-values">
                          <span className="metric-dual-item">
                            <span className="metric-sublabel">Fine:</span>
                            {currentData.sales.fine ?? "0.000g"}
                          </span>
                          <span className="metric-dual-item">
                            <span className="metric-sublabel">Amount:</span>
                            {currentData.sales.mc ?? "₹0"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="metric-right">
                      <div className="trend-container">
                        {currentData.sales.trend === "up" ? (
                          <TrendingUp size={16} className="trend-icon-up" />
                        ) : currentData.sales.trend === "down" ? (
                          <TrendingDown size={16} className="trend-icon-down" />
                        ) : null}
                        {currentData.sales.trend !== "flat" && (
                          <span
                            className={
                              currentData.sales.trend === "up"
                                ? "trend-up"
                                : "trend-down"
                            }
                          >
                            {Math.abs(currentData.sales.change)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-left">
                      <div className="metric-icon orders-icon-bg">
                        <Package size={20} color="currentColor" />
                      </div>
                      <div className="metric-info">
                        <p className="metric-value">
                          {currentData.orders.value}
                        </p>
                        <p className="metric-label">Orders</p>
                      </div>
                    </div>
                    <div className="metric-right">
                      <div className="trend-container">
                        {currentData.orders.trend === "up" ? (
                          <TrendingUp size={16} className="trend-icon-up" />
                        ) : currentData.orders.trend === "down" ? (
                          <TrendingDown size={16} className="trend-icon-down" />
                        ) : null}
                        {currentData.orders.trend !== "flat" && (
                          <span
                            className={
                              currentData.orders.trend === "up"
                                ? "trend-up"
                                : "trend-down"
                            }
                          >
                            {Math.abs(currentData.orders.change)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-left">
                      <div className="metric-icon received-icon-bg">
                        <span className="metric-icon-text">₹</span>
                      </div>
                      <div className="metric-info">
                        <div className="metric-dual-values">
                          <span className="metric-dual-item">
                            <span className="metric-sublabel">Fine:</span>
                            {currentData.received.fine ?? "0.000g"}
                          </span>
                          <span className="metric-dual-item">
                            <span className="metric-sublabel">Amount:</span>
                            {currentData.received.mc ?? "₹0"}
                          </span>
                        </div>
              
                      </div>
                    </div>
                    <div className="metric-right">
                      <div className="trend-container">
                        {currentData.received.trend === "up" ? (
                          <TrendingUp size={16} className="trend-icon-up" />
                        ) : currentData.received.trend === "down" ? (
                          <TrendingDown size={16} className="trend-icon-down" />
                        ) : null}
                        {currentData.received.trend !== "flat" && (
                          <span
                            className={
                              currentData.received.trend === "up"
                                ? "trend-up"
                                : "trend-down"
                            }
                          >
                            {Math.abs(currentData.received.change)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className="card live-rate-card"
            onClick={() => navigate("/liverates")}
          >
            <div className="live-rate-header">
              <h3 className="overview-title">Live Rate</h3>
              <span className="view-details">View details</span>
            </div>
            <div className="rate-list">
              {liveRates ? (
                <>
                  {/* Gold RTGS Sale Rate */}
                  {(() => {
                    const currentGold = liveRates.gold?.RTGS_SALE_RATE || 0;
                    const prevGold = previousRates?.gold || currentGold;
                    const goldChange = calculateChange(currentGold, prevGold);

                    return (
                      <div className="rate-item">
                        <div className="rate-left">
                          <div className="rate-dot gold-dot"></div>
                          <span className="rate-label">Gold RTGS</span>
                        </div>
                        <div className="rate-right">
                          <div className="rate-value-container">
                            <span className="rate-value">
                              {formatCurrencyINR(currentGold)}/g
                            </span>
                            {
                              <div className="rate-change-inline">
                                {goldChange.direction === "up" ? (
                                  <TrendingUp
                                    size={14}
                                    className="trend-icon-up"
                                  />
                                ) : (
                                  <TrendingDown
                                    size={14}
                                    className="trend-icon-down"
                                  />
                                )}
                                <span
                                  className={
                                    goldChange.direction === "up"
                                      ? "trend-up"
                                      : "trend-down"
                                  }
                                >
                                  {goldChange.direction === "up" ? "+" : "-"}
                                  {formatCurrencyINR(
                                    Math.abs(goldChange.amount),
                                  )}
                                </span>
                                <span
                                  className={`trend-pct ${
                                    goldChange.direction === "up"
                                      ? "trend-up"
                                      : "trend-down"
                                  }`}
                                >
                                  ({goldChange.direction === "up" ? "+" : "-"}
                                  {goldChange.percentage}%)
                                </span>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Silver RTGS Sale Rate */}
                  {(() => {
                    const currentSilver = liveRates.silver?.RTGS_SALE_RATE || 0;
                    const prevSilver = previousRates?.silver || currentSilver;
                    const silverChange = calculateChange(
                      currentSilver,
                      prevSilver,
                    );

                    return (
                      <div className="rate-item">
                        <div className="rate-left">
                          <div className="rate-dot silver-dot"></div>
                          <span className="rate-label">Silver RTGS</span>
                        </div>
                        <div className="rate-right">
                          <div className="rate-value-container">
                            <div className="rate-value">
                              {formatCurrencyINR(currentSilver)}/g
                            </div>
                            {
                              <div className="rate-change-inline">
                                {silverChange.direction === "up" ? (
                                  <TrendingUp
                                    size={14}
                                    className="trend-icon-up"
                                  />
                                ) : (
                                  <TrendingDown
                                    size={14}
                                    className="trend-icon-down"
                                  />
                                )}
                                <span
                                  className={
                                    silverChange.direction === "up"
                                      ? "trend-up"
                                      : "trend-down"
                                  }
                                >
                                  {silverChange.direction === "up" ? "+" : "-"}
                                  {formatCurrencyINR(
                                    Math.abs(silverChange.amount),
                                  )}
                                </span>
                                <span
                                  className={`trend-pct ${
                                    silverChange.direction === "up"
                                      ? "trend-up"
                                      : "trend-down"
                                  }`}
                                >
                                  ({silverChange.direction === "up" ? "+" : "-"}
                                  {silverChange.percentage}%)
                                </span>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="rate-item">
                  <span className="rate-label">Loading rates...</span>
                </div>
              )}
            </div>
          </div>

          <div className="service-cards-grid">
            <CatalogueCard />

            <div
              className="service-card ready-stock-card"
              onClick={() => navigate("/readystockservices")}
              style={{
                backgroundImage: `linear-gradient(90deg, #ea7317 0%, hsla(0, 0%, 100%, 0.25) 100%), url("/ready.png")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <div className="service-overlay"></div>
              <div className="service-content">
                <h3 className="service-title">Ready-Stock Services</h3>
                <p className="service-subtitle">
                  Add ready items, manage listings
                </p>
              </div>
            </div>

            <div
              className="service-card order-serv"
              onClick={() => navigate("/orderserve")}
              style={{
                backgroundImage: `linear-gradient(90deg, #c2410c 0%, hsla(0, 0%, 100%, 0.25) 100%), url("/order.png")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <div className="service-overlay"></div>
              <div className="service-content">
                <h3 className="service-title">Order Services</h3>
                <p className="service-subtitle">Custom orders & quotations</p>
              </div>
            </div>

            <div
              className="service-card market-card"
              onClick={() => navigate("/openmarket")}
             style={{
                backgroundImage: `linear-gradient(90deg, #e11d48 0%, hsla(0, 0%, 100%, 0.25) 100%), url("/open.png")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <div className="service-overlay"></div>
              <div className="service-content">
                <h3 className="service-title">Open Market Services</h3>
                <p className="service-subtitle">Browse superior listings</p>
              </div>
            </div>
          </div>

          <div className="card offers-card">
            <div className="offers-header">
              <h3 className="overview-title">Offers & Discounts</h3>
              <span className="create-offer">Create Offer</span>
            </div>
            <div className="offers-grid">
              <div className="offer-card festival-card">
                <div className="offer-small-text">Festival Sale</div>
                <div className="offer-large-text">25% OFF</div>
                <div className="offer-description">
                  25% Off on All Jewellery Making charges
                </div>
              </div>
              <div className="offer-card diamond-card">
                <div className="offer-small-text">Limited Time</div>
                <div className="offer-large-text">Diamond Collection</div>
                <div className="offer-description">
                  Exclusive diamond jewellery sets
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
};

export default Dashboard;
